import React, { useEffect, useMemo, useState } from 'react';
import { LegalLinkItems } from './legalLinks';
import { Link, useNavigate } from 'react-router-dom';

import '../../App.css';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { fmtDate, fmtDateTime } from '../../utils/ruDates';

function parseTosSections(text) {
  if (!text) {
    return [];
  }

  const blocks = text
    .split(/\n\s*\n/g)
    .map((block) => block.trim())
    .filter(Boolean);

  return blocks.map((block, index) => {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    const [heading, ...bodyLines] = lines;
    const body = bodyLines.join(' ');

    return {
      id: `tos-section-${index}`,
      heading,
      body: body || heading,
      standalone: bodyLines.length === 0,
    };
  });
}

function TermsOfServicePage() {
  const navigate = useNavigate();
  const { request, showStatus } = useApi();
  const { isAuthenticated } = useAuth();
  const [currentTos, setCurrentTos] = useState(null);
  const [acceptance, setAcceptance] = useState(null);
  const [acceptBusy, setAcceptBusy] = useState(false);

  useEffect(() => {
    let ignore = false;

    const loadPageData = async () => {
      // Оферта — публичный документ, должен отображаться без входа в аккаунт.
      // Статус принятия условий — вторичные данные только для авторизованных
      // пользователей: их сбой не должен скрывать текст оферты.
      try {
        const tosPayload = await request('/api/v1/legal/tos/current');
        if (!ignore) {
          setCurrentTos(tosPayload);
        }
      } catch (error) {
        if (!ignore) {
          showStatus('error', 'Не удалось загрузить актуальную редакцию условий.');
        }
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      try {
        const acceptancePayload = await request('/api/v1/legal/tos/acceptance-status');
        if (!ignore) {
          setAcceptance(acceptancePayload);
        }
      } catch (error) {
        // Игнорируем: страница оферты остаётся доступной даже если статус
        // принятия условий не удалось получить (например, истёкшая сессия).
      }
    };

    loadPageData();

    return () => {
      ignore = true;
    };
  }, [isAuthenticated, request, showStatus]);

  const sections = useMemo(() => {
    const parsed = parseTosSections(currentTos?.text);
    if (parsed[0]?.standalone && parsed[0]?.body === currentTos?.title) {
      return parsed.slice(1);
    }
    return parsed;
  }, [currentTos?.text, currentTos?.title]);

  const handleAccept = async () => {
    if (!currentTos?.version) {
      return;
    }

    setAcceptBusy(true);
    try {
      const accepted = await request('/api/v1/legal/tos/accept', {
        method: 'POST',
        body: JSON.stringify({ version: currentTos.version }),
      });
      const statusPayload = await request('/api/v1/legal/tos/acceptance-status');
      setAcceptance(statusPayload);

      if (statusPayload?.current_version) {
        window.sessionStorage.setItem(`anylex_tos_notice_${statusPayload.current_version}`, '1');
        window.sessionStorage.setItem(`anylex_tos_redirect_${statusPayload.current_version}`, '1');
      }

      showStatus(
        'success',
        `Актуальная версия условий принята${accepted?.version ? `: ${accepted.version}` : '.'}`
      );
    } catch (error) {
      showStatus('error', error.message || 'Не удалось зафиксировать принятие условий.');
    } finally {
      setAcceptBusy(false);
    }
  };

  const effectiveDateLabel = currentTos?.effective_from
    ? fmtDate(currentTos.effective_from)
    : '15.07.2026';

  const requiresAcceptance = isAuthenticated && currentTos && (!acceptance?.accepted || acceptance?.requires_reacceptance);

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <button
          type="button"
          className="legal-page__back"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
        >
          &larr; Назад
        </button>
        <Link to="/" className="legal-page__logo">Энилекс.Контроль</Link>
      </div>

      <div className="legal-page__content">
        <h1>{currentTos?.title || 'Публичная оферта'}</h1>
        <p className="legal-page__subtitle">
          на оказание услуг по предоставлению доступа к сервису «Энилекс. Контроль.»
        </p>
        <p className="legal-page__date">
          Версия {currentTos?.version || '2026-07-15'}, действует с {effectiveDateLabel}
        </p>

        {acceptance?.requires_reacceptance && (
          <div className="panel panel--warning">
            Доступна новая редакция условий. Для продолжения работы с аккаунтом нужно принять актуальную версию.
          </div>
        )}

        {acceptance?.accepted && !acceptance?.requires_reacceptance && (
          <div className="panel panel--success">
            Для вашего аккаунта уже зафиксировано принятие актуальной версии условий.
            {acceptance?.accepted_at ? ` ${fmtDateTime(acceptance.accepted_at)}` : ''}
          </div>
        )}

        {requiresAcceptance && (
          <div className="panel">
            <h3>Подтверждение актуальной редакции</h3>
            <p className="muted">
              Подтвердите принятие версии {currentTos.version}. Событие будет записано в audit trail.
            </p>
            <button type="button" onClick={handleAccept} disabled={acceptBusy}>
              {acceptBusy ? 'Сохраняем...' : 'Принять условия'}
            </button>
          </div>
        )}

        {!isAuthenticated && (
          <div className="panel">
            <h3>Принятие условий доступно после входа</h3>
            <p className="muted">
              Авторизуйтесь в аккаунте, если нужно зафиксировать принятие актуальной версии условий для пользователя.
            </p>
          </div>
        )}

        {sections.map((section) => (
          <section key={section.id}>
            {section.standalone ? (
              <p>{section.body}</p>
            ) : (
              <>
                <h2>{section.heading}</h2>
                <p>{section.body}</p>
              </>
            )}
          </section>
        ))}

        {Array.isArray(currentTos?.requisites) && currentTos.requisites.length > 0 && (
          <section>
            <h2>12. Реквизиты Исполнителя</h2>
            <div className="legal-page__requisites">
              {currentTos.requisites.map((group) => (
                <React.Fragment key={group.title}>
                  <h3>{group.title}</h3>
                  {group.rows.map(([label, value]) => (
                    <p key={label}><strong>{label}:</strong> {value}</p>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </section>
        )}

        {acceptance?.accepted_at && (
          <section>
            <p className="muted">
              Последнее принятие условий: {fmtDateTime(acceptance.accepted_at)}
            </p>
          </section>
        )}
      </div>

      <div className="legal-page__footer">
        <Link to="/">Вернуться на главную</Link>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 18px", marginTop: 12 }}>
          <LegalLinkItems short />
        </div>
      </div>
    </div>
  );
}

export default TermsOfServicePage;
